import { gql, useMutation } from "@apollo/client";
import { InputField, InputTextAreaField } from "@src/components/fields";
import { StaticSelectField } from "@src/components/fields/StaticSelect";
import Form from "@src/components/Form";
import CountrySelect from "@src/components/select/CountrySelect";
import {
    type GQLCreatePackageInput,
    type GQLCreatePackageMutation,
    type GQLCreatePackageMutationVariables,
    GQLPackageType,
} from "@src/graphql.generated";
import { type FC, type PropsWithChildren } from "react";
import { FormattedMessage, useIntl } from "react-intl";

// eslint-disable-next-line @comet/no-private-sibling-import
import { EndAdornment } from "../edit/Form.sc";

const createPackageMutation = gql`
    mutation CreatePackage($input: CreatePackageInput!) {
        createPackage(input: $input) {
            id
        }
    }
`;

const AddForm: FC<PropsWithChildren> = () => {
    const intl = useIntl();
    const [createMutation] = useMutation<GQLCreatePackageMutation, GQLCreatePackageMutationVariables>(createPackageMutation);

    const submit = ({ country, name, description, type, validTimeInMonths }: GQLCreatePackageInput) => {
        return createMutation({
            variables: {
                input: {
                    country,
                    name,
                    description,
                    type,
                    validTimeInMonths: Number(validTimeInMonths),
                },
            },
        });
    };

    const packageTypeOptions = [
        { value: GQLPackageType.Basic, label: intl.formatMessage({ id: "reviermanager.package.type.baisc", defaultMessage: "Basic" }) },
        { value: GQLPackageType.Premium, label: intl.formatMessage({ id: "reviermanager.package.type.premium", defaultMessage: "Premium" }) },
    ];

    return (
        <Form
            mode="add"
            onSubmit={submit}
            title={intl.formatMessage({ id: "reviermanager.packages.form.add.title", defaultMessage: "Neues Paket erstellen" })}
        >
            <StaticSelectField
                required
                name="country"
                label={intl.formatMessage({ id: "reviermanager.package.country", defaultMessage: "Land" })}
                component={CountrySelect}
                labelVariant="long"
            />
            <InputField name="name" label={intl.formatMessage({ id: "reviermanager.package.name", defaultMessage: "Paketname" })} />
            <InputTextAreaField
                required
                name="description"
                label={intl.formatMessage({ id: "reviermanager.package.description", defaultMessage: "Beschreibung" })}
            />
            <StaticSelectField
                required
                name="type"
                label={intl.formatMessage({ id: "reviermanager.package.type", defaultMessage: "Typ" })}
                options={packageTypeOptions}
            />
            <InputField
                name="validTimeInMonths"
                type="number"
                label={intl.formatMessage({ id: "reviermanager.package.validTimeInMonths", defaultMessage: "Gültig für" })}
                endAdornment={
                    <EndAdornment>
                        <FormattedMessage id="reviermanager.package.add.months" defaultMessage="Monat(e)" />
                    </EndAdornment>
                }
            />
        </Form>
    );
};

export default AddForm;
